import React from 'react';
import type {
  AhqSubmission,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import {
  CondensedHeader,
  Form,
  Gutter,
  Layout,
  Sortable,
  SortableList,
  List,
  ListItem,
  SmallHeader,
} from '@axiom/ui';

import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';
import EmptyCandidates from '../../public/svg/empty-candidates.svg';

import { OpportunitySubmissionUtil } from './opportunity-submission-util';
import { OpportunitySubmissionCandidateListItem } from './OpportunitySubmissionCandidateListItem';
import { OpportunitySubmissionCandidateListSchema } from './opportunity-submission-candidate-list-schema';

export type OpportunitySubmissionCandidateListProps = {
  opportunity: Opportunity;
  readOnly?: boolean;
  selectedCandidateId: string;
  submission: AhqSubmission;
  candidates: OpportunityCandidate[];
  onSelectedCandidateChange: (candidateId: string) => void;
  name?: string;
};

export const OpportunitySubmissionCandidateList = ({
  submission,
  candidates,
  selectedCandidateId,
  onSelectedCandidateChange,
  readOnly = false,
  opportunity,
  name = 'TALENT_LIST',
}: OpportunitySubmissionCandidateListProps) => {
  if (candidates.length === 0) {
    return null;
  }

  const {
    activeCuratedCandidates,
    pendingCuratedCandidates,
    afcSubmittedCandidates,
  } = OpportunitySubmissionUtil.groupCandidates({
    candidates,
    opportunity,
  });
  const candidateIds = activeCuratedCandidates.map(c => c.id);

  // Prepare options for Sortable - only active curated candidates with display content
  const curatedCandidateOptions = activeCuratedCandidates.map(candidate => ({
    ...candidate,
    display: (
      <OpportunitySubmissionCandidateListItem
        candidate={candidate}
        submission={submission}
      />
    ),
  }));

  const handleCandidateReorder = async ({
    candidateIds: reorderedIds,
  }: {
    candidateIds?: string[] | null;
  }) => {
    if (!reorderedIds) return;

    const candidatePatches =
      OpportunitySubmissionUtil.buildSubmissionRankPatches({
        candidates,
        opportunity,
        reorderedCandidateIds: reorderedIds,
      });

    if (candidatePatches.length > 0) {
      await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
        newSubmissionType: submission.newSubmissionType,
        candidates: candidatePatches,
      });
    }

    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId: submission.submissionId,
      opportunityId: opportunity.id,
    });
  };

  return (
    <div data-test={name}>
      <SmallHeader name="PENDO_CURATED_TALENT_HEADER">
        Curated talent
      </SmallHeader>
      <Gutter bottom="16px" />
      <div data-test="PENDO_CURATED_TALENT_LIST">
        {activeCuratedCandidates.length === 0 ? (
          <Gutter top="24px" bottom="20px">
            <Layout
              position="middle center"
              direction="vertical"
              verticalGutter="16px"
            >
              <EmptyCandidates width="100px" height="79px" />
              <CondensedHeader>No Curated Talent</CondensedHeader>
            </Layout>
          </Gutter>
        ) : (
          <Form
            schema={OpportunitySubmissionCandidateListSchema}
            initialValues={{ candidateIds }}
            onSubmit={formData => {
              void handleCandidateReorder(formData);
            }}
            submitOnChange={!readOnly}
          >
            {() => (
              <Sortable
                displayKey="display"
                valueKey="id"
                options={curatedCandidateOptions}
                shouldHighlight={candidate =>
                  candidate.id === selectedCandidateId
                }
                onItemClicked={candidate => {
                  onSelectedCandidateChange(candidate.id);
                }}
              >
                <SortableList name="candidateIds" hideHandle={readOnly} />
              </Sortable>
            )}
          </Form>
        )}
      </div>
      <Gutter bottom="24px" />
      {afcSubmittedCandidates.length > 0 && (
        <div data-test="SELF_SUBMITTED_TALENT">
          <SmallHeader name="PENDO_SELF_SUBMITTED_TALENT_HEADER">
            Self-Submitted Talent
          </SmallHeader>
          <Gutter bottom="16px" />
          <List name="PENDO_SELF_SUBMITTED_TALENT_LIST">
            {afcSubmittedCandidates.map((candidate, index) => (
              <ListItem
                key={candidate.id}
                name={`SUBMISSION_CANDIDATE_${index}`}
                onClick={() => {
                  onSelectedCandidateChange(candidate.id);
                }}
                selected={candidate.id === selectedCandidateId}
              >
                <OpportunitySubmissionCandidateListItem
                  candidate={candidate}
                  submission={submission}
                />
              </ListItem>
            ))}
          </List>
          <Gutter bottom="24px" />
        </div>
      )}
      {pendingCuratedCandidates.length > 0 && (
        <div data-test="PENDING_TALENT">
          <SmallHeader name="PENDO_PENDING_TALENT_HEADER">
            Not on Submission
          </SmallHeader>
          <Gutter bottom="16px" />
          <List name="PENDO_PENDING_TALENT_LIST">
            {pendingCuratedCandidates.map((candidate, index) => (
              <ListItem
                key={candidate.id}
                name={`PENDING_CANDIDATE_${index}`}
                onClick={() => {
                  onSelectedCandidateChange(candidate.id);
                }}
                selected={candidate.id === selectedCandidateId}
              >
                <OpportunitySubmissionCandidateListItem
                  candidate={candidate}
                  submission={submission}
                />
              </ListItem>
            ))}
          </List>
          <Gutter bottom="24px" />
        </div>
      )}
    </div>
  );
};
